import { useRef, useCallback, useMemo, useState, useEffect, type ReactNode } from 'react';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ChatBubbleOutlineIcon, HistoryIcon } from '@/components/ui/Icons';
import { ProjectChangeHistoryPopover } from '@/features/project-change-history/ProjectChangeHistoryPopover';
import { setLastUndoRedoProjectId } from '@/hooks/useProjectUndoRedo';
import { CellCommentShellProvider } from './cellCommentShellContext';
import { CellCommentCornerBadge } from './CellCommentCornerBadge';
import {
  useCellCommentsTable,
  type ActiveCellCommentThread,
  type CellCommentThreadTarget,
} from './CellCommentsTableContext';

interface TableCellCommentShellProps {
  target: CellCommentThreadTarget;
  commentCount: number;
  children: ReactNode;
  /** Угловой оверлей в ячейке (например бейдж «Без правок» в name-cell) */
  cornerLeading?: ReactNode;
  changeHistory?: {
    projectId: string;
    projectName: string;
    columnId?: string;
    columnIds?: string[];
    title?: string;
  };
}

export function TableCellCommentShell({
  target,
  commentCount,
  children,
  cornerLeading,
  changeHistory,
}: TableCellCommentShellProps) {
  const { open, notifyThreadUnmount, isPinnedThread, cancelHoverDismiss, scheduleHoverDismiss } =
    useCellCommentsTable();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const hasComments = commentCount > 0;

  const threadRefForUnmount = useRef<ActiveCellCommentThread | null>(null);
  threadRefForUnmount.current = {
    target,
    triggerRef: wrapperRef,
    mode: 'pinned',
  };

  useEffect(
    () => () => {
      const thread = threadRefForUnmount.current;
      if (thread) notifyThreadUnmount(thread);
    },
    [notifyThreadUnmount, target]
  );

  const openThread = useCallback(() => {
    open({ target, triggerRef: wrapperRef, mode: 'pinned' });
  }, [open, target]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  }, []);

  const handleBadgeOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      cancelHoverDismiss();
      open({ target, triggerRef: wrapperRef, mode: 'pinned' });
    },
    [cancelHoverDismiss, open, target]
  );

  const handleBadgePointerEnter = useCallback(() => {
    cancelHoverDismiss();
    if (isPinnedThread(target)) return;
    open({ target, triggerRef: wrapperRef, mode: 'hover' });
  }, [cancelHoverDismiss, isPinnedThread, open, target]);

  const commentMenuLabel = hasComments ? 'Посмотреть комментарии' : 'Добавить комментарий';
  const menuItem = useMemo(
    () => ({
      label: commentMenuLabel,
      icon: <ChatBubbleOutlineIcon size={18} />,
      onClick: openThread,
    }),
    [commentMenuLabel, openThread]
  );

  const historyMenuItem = useMemo(
    () =>
      changeHistory
        ? {
            label: 'Посмотреть изменения',
            icon: <HistoryIcon size={18} />,
            onClick: () => {
              setContextMenuOpen(false);
              setLastUndoRedoProjectId(changeHistory.projectId);
              setHistoryOpen(true);
            },
          }
        : null,
    [changeHistory]
  );

  const contextMenuItems = useMemo(
    () => (historyMenuItem ? [menuItem, historyMenuItem] : [menuItem]),
    [historyMenuItem, menuItem]
  );

  const contextValue = useMemo(
    () => ({
      addCommentMenuItem: menuItem,
      commentBadge: null,
    }),
    [menuItem]
  );

  return (
    <CellCommentShellProvider value={contextValue}>
      <div ref={wrapperRef} className="project-table-cell-comment-shell" onContextMenu={handleContextMenu}>
        {children}
        {cornerLeading}
        {hasComments ? (
          <CellCommentCornerBadge
            onOpen={handleBadgeOpen}
            onPointerEnter={handleBadgePointerEnter}
            onPointerLeave={scheduleHoverDismiss}
          />
        ) : null}
        <ContextMenu
          isOpen={contextMenuOpen}
          onClose={() => setContextMenuOpen(false)}
          anchorPosition={contextMenuPos}
          items={contextMenuItems}
        />
        {changeHistory ? (
          <ProjectChangeHistoryPopover
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            anchorEl={wrapperRef.current}
            anchorPlacement="below-cell"
            projectId={changeHistory.projectId}
            projectName={changeHistory.projectName}
            columnId={changeHistory.columnId}
            columnIds={changeHistory.columnIds}
            title={changeHistory.title}
          />
        ) : null}
      </div>
    </CellCommentShellProvider>
  );
}
